import axios from 'axios'
import { getPlatformApiBase } from '../../utils/apiBase'
import { readGclid } from '../../utils/attribution'

// Trailing slash stripped: these URLs are built by string concatenation with '/...' paths,
// and a base like 'http://localhost:8000/' would otherwise produce '//signup/...' → 404.
const PLATFORM_API_BASE = getPlatformApiBase().replace(/\/+$/, '')

export interface SignupProfile {
    name_given?: string
    name_family?: string
}

// The three named tiers marketing sells (MD-1655) — functionally identical today, distinct
// only in which Stripe Price the checkout session charges. Matches the backend's own Plan
// type (signup/model.py) exactly; DEFAULT_PLAN mirrors its DEFAULT_PLAN for the same reason:
// an old marketing link with no `plan` query param should keep getting exactly what it
// always got, not silently start charging something else.
export type Plan = 'light' | 'professional' | 'premium'
const DEFAULT_PLAN: Plan = 'professional'
const PLANS: readonly Plan[] = ['light', 'professional', 'premium']

export function parsePlan(value: string | null): Plan {
    return (PLANS as readonly string[]).includes(value ?? '') ? (value as Plan) : DEFAULT_PLAN
}

export function getSignupErrorDetail(error: unknown): string | undefined {
    return axios.isAxiosError<{ detail?: string }>(error) ? error.response?.data?.detail : undefined
}

// Lets the login page hide its "start your free trial" link, and the signup page itself
// show an honest "not available" state instead of a form that would 503 on submit, while
// the backend's signup_feature_flag kill switch is off.
export async function isSignupEnabled(): Promise<boolean> {
    try {
        const { data } = await axios.get<{ enabled: boolean }>(`${PLATFORM_API_BASE}/signup/status`)
        return data.enabled
    } catch (error) {
        console.warn('[Signup] Failed to check signup availability:', error)
        return false
    }
}

// Login mounts far more often than Signup does (every logout, every unauthenticated page
// load) — cached at module scope, next to the fetch it wraps, so the same page load
// doesn't re-fetch this on every mount, only once. Reset by a full reload, same as the
// feature flag itself needing one to be noticed. Tests reset this between cases via
// vi.resetModules() + a dynamic re-import, not a test-only export.
let signupEnabledCache: Promise<boolean> | null = null
export function getSignupEnabledCached(): Promise<boolean> {
    if (!signupEnabledCache) signupEnabledCache = isSignupEnabled()
    return signupEnabledCache
}

// ── Email OTP signup: prove inbox ownership BEFORE the account exists ──
// (verify-then-password pattern; codes instead of links because corporate mail
// scanners consume one-time links). All three calls are unauthenticated.

export async function sendSignupCode(email: string, language: string): Promise<void> {
    // language localizes the code email (ISO 639-1); the backend falls back to English.
    await axios.post(`${PLATFORM_API_BASE}/signup/send-code`, { email, language })
}

export async function verifySignupCode(email: string, code: string): Promise<string> {
    const { data } = await axios.post<{ signup_token: string }>(`${PLATFORM_API_BASE}/signup/verify-code`, {
        email,
        code,
    })
    return data.signup_token
}

// Spread into the body of either checkout-session request. Attribution belongs to the ad
// click, not to anything the user typed, so it is picked up here rather than threaded
// through the signup form — and picked up by both entry points, since an ad click can just
// as easily end in an SSO signup as in a password one. Yields nothing at all when there is
// no gclid, so a signup that did not come from an ad posts exactly the body it always did.
function gclidField(): { gclid?: string } {
    const gclid = readGclid()
    return gclid ? { gclid } : {}
}

// One Stripe call, made here: creates the Checkout Session that collects payment info
// and starts the trial. The password is hashed server-side immediately and bridged
// across the Stripe redirect — it never appears in any later request.
export async function createCheckoutSession(
    signupToken: string,
    password: string,
    profile: Required<SignupProfile>,
    plan: Plan
): Promise<string> {
    const { data } = await axios.post<{ url: string }>(`${PLATFORM_API_BASE}/signup/checkout-session`, {
        signup_token: signupToken,
        password,
        ...profile,
        ...gclidField(),
        plan,
    })
    return data.url
}

// SSO signup: the caller is already Firebase-authenticated (Google/Microsoft sign-in already
// verified the email), so this skips send-code/verify-code entirely — the id token itself is
// the proof, attached here manually since this page renders outside <App/> and never populates
// the shared auth store/api.ts's interceptor.
export async function createSsoCheckoutSession(
    idToken: string,
    profile: Required<SignupProfile>,
    plan: Plan
): Promise<string> {
    const { data } = await axios.post<{ url: string }>(
        `${PLATFORM_API_BASE}/signup/sso-checkout-session`,
        { ...profile, ...gclidField(), plan },
        { headers: { Authorization: `Bearer ${idToken}` } }
    )
    return data.url
}

// Called once Stripe redirects back to the app's success_url. The backend verifies the
// Checkout Session server-side and only then creates the Firebase user + solo record —
// so this is also where the account is actually created, not at the step above. Returns a
// Firebase custom token: the browser has no password and no session at this point (a full
// page navigation to Stripe's domain and back wipes any React/local state), so the backend
// signs the fresh account in directly rather than asking the user to type their password again.
export async function completeSignup(sessionId: string): Promise<string> {
    const { data } = await axios.post<{ custom_token: string }>(`${PLATFORM_API_BASE}/signup/complete`, {
        session_id: sessionId,
    })
    return data.custom_token
}
